import React, { useRef, useState, useEffect } from 'react';
import MonacoEditor from '@monaco-editor/react';
import Styles from './APIBody.module.scss';
import { useTranslation } from 'components/lib';
import { v4 as uuidv4 } from 'uuid';

export function APIBody({ isResponse = false, reqBody, setReqBody, onUpdateClick }) {
    const { t } = useTranslation();
    const editorRef = useRef(null);
    const [sidebarOffset, setSidebarOffset] = useState(8);

    const editorPath = useRef(isResponse ? 'response.json' : `request-${uuidv4()}.json`);

    const handleEditorChange = (value) => {
        if (!isResponse) {
            if (typeof setReqBody === 'function') setReqBody(value);
            if (typeof onUpdateClick === 'function') onUpdateClick(value);
        }
    };

    const handleEditorMount = (editor, monaco) => {
        editorRef.current = editor;
    };

    const handleFormatCode = () => {
        if (editorRef.current) {
            requestAnimationFrame(() => {
                editorRef.current?.getAction('editor.action.formatDocument')?.run();
            });
        }
    };

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.layout();
        }
    }, [sidebarOffset]);

    useEffect(() => {
        const updateOffset = () => {
            const drawer = document.querySelector('.MuiDrawer-paper');
            if (drawer && drawer.offsetWidth > 0 && drawer.getBoundingClientRect().left >= 0) {
                setSidebarOffset(drawer.offsetWidth + 8);
            } else {
                setSidebarOffset(8);
            }
        };

        updateOffset();
        window.addEventListener('resize', updateOffset);

        const observer = new MutationObserver(updateOffset);
        const drawerContainer = document.querySelector('#root'); // app root
        if (drawerContainer) observer.observe(drawerContainer, { childList: true, subtree: true });

        return () => {
            window.removeEventListener('resize', updateOffset);
            observer.disconnect();
        };
    }, []);

    const editorValue = typeof reqBody === 'string' ? reqBody : JSON.stringify(reqBody ?? {}, null, 4);

    return (
        <div
            id="bodyParent"
            style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
        >
            <div className={Styles.editorBox} style={{ width: '100%', position: 'relative' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '6px 12px',
                }}>
                    {!isResponse && (
                        <button
                            id="prettifyBtn"
                            onClick={handleFormatCode}
                            style={{
                                padding: '5px 10px',
                                fontSize: '0.6875em',
                                cursor: 'pointer',
                                color: '#1450a0',
                                fontWeight: 'bold',
                            }}
                        >
                            {t('Components.NodeLayout.NodeTopostman.body.prettifyBtn')}
                        </button>
                    )}
                </div>

                <div
                    style={{
                        borderTop: '1px solid #dcdbdb',
                        borderRadius: '4px 4px 0 0',
                    }}
                >
                    <MonacoEditor
                        path={editorPath.current}
                        value={editorValue}
                        height="40vh"
                        width="100%"
                        theme="vs-light"
                        defaultLanguage="json"
                        onChange={handleEditorChange}
                        onMount={handleEditorMount}
                        options={{
                            readOnly: isResponse,
                            minimap: { enabled: false },
                            scrollbar: { vertical: 'auto', alwaysConsumeMouseWheel: false },
                            fontSize: 14,
                            wordWrap: 'on',
                            glyphMargin: false,
                            folding: false,
                            lineNumbersMinChars: 2,
                            overviewRulerLanes: 0,
                        }}
                    />
                </div>
            </div>
        </div>
    );
}